import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { fetchStoredBytes } from '../utils/fileUrls';

interface CompanySettings {
  id: string;
  company_name: string;
  logo_url: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  tax_id: string | null;
  dot_number: string | null;
  mc_number: string | null;
}

export function useCompanySettings() {
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  // Object URL currently used as the favicon, revoked when replaced.
  const faviconObjectUrl = useRef<string | null>(null);

  const loadCompanySettings = async () => {
    try {
      const { data, error } = await supabase
        .from('company_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setCompanySettings(data);
        
        // Update document title
        if (data.company_name) {
          document.title = `${data.company_name} - Transportation Management System`;
        }

        // Update favicon
        if (data.logo_url) {
          updateFavicon(data.logo_url);
        }
      }
    } catch (error) {
      console.error('Error loading company settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // The logo bucket is private, so the stored URL cannot go into <head>
  // directly. The bytes are downloaded and served from a blob: URL, which also
  // keeps a signed token out of the DOM. Before login (this hook runs ahead of
  // the auth gate) the download is refused and the default favicon stays.
  //
  // og:image / twitter:image are no longer rewritten: link-preview crawlers do
  // not run JS and read the static tags in index.html, so the runtime rewrite
  // never reached them.
  const updateFavicon = async (logoUrl: string) => {
    let objectUrl: string;
    try {
      objectUrl = URL.createObjectURL(await fetchStoredBytes(logoUrl));
    } catch {
      return;
    }

    let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = objectUrl;

    let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement;
    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement('link');
      appleTouchIcon.rel = 'apple-touch-icon';
      document.head.appendChild(appleTouchIcon);
    }
    appleTouchIcon.href = objectUrl;

    if (faviconObjectUrl.current) URL.revokeObjectURL(faviconObjectUrl.current);
    faviconObjectUrl.current = objectUrl;
  };

  useEffect(() => {
    loadCompanySettings();

    // Listen for company settings updates
    const handleCompanyUpdate = () => {
      loadCompanySettings();
    };

    const handleLogoUpdate = () => {
      loadCompanySettings();
    };

    window.addEventListener('companyUpdated', handleCompanyUpdate);
    window.addEventListener('logoUpdated', handleLogoUpdate);

    // This hook first runs before login, when the private logo cannot be read.
    // Load again once a user signs in (and when the user changes), so the tab
    // icon appears without a reload. Not on every SIGNED_IN: Supabase emits one
    // on each tab refocus. Deferred with setTimeout because calling Supabase
    // from inside the auth callback can deadlock supabase-js.
    let lastUserId: string | null | undefined;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user?.id ?? null;
      if (lastUserId !== undefined && userId && userId !== lastUserId) {
        setTimeout(loadCompanySettings, 0);
      }
      lastUserId = userId;
    });

    return () => {
      window.removeEventListener('companyUpdated', handleCompanyUpdate);
      window.removeEventListener('logoUpdated', handleLogoUpdate);
      subscription.unsubscribe();
    };
  }, []);

  return { companySettings, loading, refresh: loadCompanySettings };
}
